import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, Paperclip, User, Search } from "lucide-react";

const TEMPLATE_OPTIONS = [
  { key: "cash_offer", label: "Cash Offer Sent" },
  { key: "follow_up", label: "Follow-up" },
  { key: "contract_executed", label: "Contract Executed — Next Steps" },
  { key: "cold_outreach", label: "Cold Outreach" },
];

/**
 * Reusable email compose dialog.
 * Pass leadId OR propertyId (for template pre-fill) and offerId (for PDF attach).
 * onSent(commId) fires after successful send.
 */
export default function EmailComposeDialog({
  open,
  onOpenChange,
  leadId: initialLeadId = null,
  propertyId = null,
  offerId = null,
  defaultTemplate = "cash_offer",
  defaultRecipient = "",
  defaultRecipientName = "",
  onSent,
}) {
  const [tpl, setTpl] = useState(defaultTemplate);
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachOffer, setAttachOffer] = useState(!!offerId);
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  // Lead picker (only used when opened without an explicit lead)
  const [leadId, setLeadId] = useState(initialLeadId);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("");
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => { setLeadId(initialLeadId); }, [initialLeadId]);

  // Fetch leads once when opening without a pre-selected lead.
  useEffect(() => {
    if (!open || initialLeadId) return;
    setLoadingLeads(true);
    api.get("/leads")
      .then((r) => setLeads((r.data || []).filter((l) => l.email || l.property_address)))
      .finally(() => setLoadingLeads(false));
  }, [open, initialLeadId]);

  // When user picks a lead from the dropdown, fill recipient with their email.
  useEffect(() => {
    if (initialLeadId) return; // externally-controlled — leave recipient alone
    if (!leadId) return;
    const l = leads.find((x) => x.id === leadId);
    if (l?.email) setRecipient(l.email);
  }, [leadId, leads, initialLeadId]);

  const filteredLeads = useMemo(() => {
    const q = leadFilter.trim().toLowerCase();
    if (!q) return leads.slice(0, 100);
    return leads.filter((l) =>
      [l.name, l.property_address, l.email, l.phone]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    ).slice(0, 50);
  }, [leads, leadFilter]);

  useEffect(() => {
    setRecipient(defaultRecipient);
  }, [defaultRecipient]);

  // Fetch template preview when template / lead / property changes.
  useEffect(() => {
    if (!open) return;
    setPreviewBusy(true);
    api
      .post("/emails/preview", {
        template_key: tpl,
        lead_id: leadId,
        property_id: propertyId,
        offer_id: offerId,
        recipient_email: recipient || null,
        recipient_first_name: defaultRecipientName || null,
      })
      .then((r) => {
        setSubject(r.data.subject);
        setBody(r.data.html_body);
      })
      .catch(() => toast.error("Template preview failed"))
      .finally(() => setPreviewBusy(false));
  }, [tpl, leadId, propertyId, offerId, open]);

  const send = async () => {
    if (!recipient.trim()) {
      toast.error("Recipient email required");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/emails/send", {
        recipient_email: recipient.trim(),
        subject: subject.trim(),
        html_body: body,
        lead_id: leadId,
        property_id: propertyId,
        template_key: tpl,
        attach_offer_id: attachOffer ? offerId : null,
      });
      toast.success(
        leadId
          ? "Email sent — logged on the lead's timeline"
          : "Email sent",
      );
      onSent?.(data.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-2xl max-w-2xl"
        data-testid="email-compose-dialog"
      >
        <DialogHeader>
          <DialogTitle>Send email</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Lead picker — only when opened stand-alone (Communications page) */}
          {!initialLeadId && (
            <div className="rounded-md border border-[var(--app-line)] bg-[var(--app-surface)] p-3"
                 data-testid="email-lead-picker">
              <Label className="text-xs flex items-center gap-1">
                <User size={11} /> Pick a lead from your pipeline
              </Label>
              <div className="relative mt-1">
                <Search size={11} className="absolute left-2 top-3 text-muted-foreground" />
                <Input
                  value={leadFilter}
                  onChange={(e) => setLeadFilter(e.target.value)}
                  placeholder="Search by name, address, email…"
                  className="pl-7 text-sm"
                  data-testid="email-lead-search"
                />
              </div>
              <select
                value={leadId || ""}
                onChange={(e) => setLeadId(e.target.value || null)}
                className="mt-2 w-full h-10 rounded-md border border-[var(--app-line)] px-3 text-sm bg-white"
                data-testid="email-lead-select"
              >
                <option value="">— Or send without linking to a lead —</option>
                {loadingLeads ? (
                  <option disabled>Loading…</option>
                ) : filteredLeads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {(l.name || "Lead")}
                    {l.property_address ? ` — ${l.property_address.slice(0, 60)}` : ""}
                    {l.email ? ` (${l.email})` : ""}
                  </option>
                ))}
              </select>
              {leadId && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  When you send, this email will show on the lead&apos;s activity timeline in the pipeline.
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Template</Label>
            <Select value={tpl} onValueChange={setTpl}>
              <SelectTrigger
                data-testid="email-template-select"
                className="field-sharp mt-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_OPTIONS.map((t) => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>To</Label>
            <Input
              data-testid="email-recipient"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="field-sharp mt-1"
              placeholder="seller@example.com"
            />
          </div>
          <div>
            <Label>Subject</Label>
            <Input
              data-testid="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="field-sharp mt-1"
            />
          </div>
          <div>
            <Label>Message (HTML supported)</Label>
            <Textarea
              data-testid="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="field-sharp mt-1 font-mono text-xs h-56"
            />
            {previewBusy && (
              <div className="text-[10px] text-[var(--app-text-muted)] mt-1 flex items-center gap-1">
                <Loader2 size={10} className="animate-spin" /> Loading template…
              </div>
            )}
          </div>
          {offerId && (
            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              data-testid="email-attach-offer"
            >
              <input
                type="checkbox"
                checked={attachOffer}
                onChange={(e) => setAttachOffer(e.target.checked)}
              />
              <Paperclip size={13} />
              Attach cash offer PDF
            </label>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="app-btn app-btn-ghost"
            data-testid="email-cancel"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={busy}
            className="app-btn app-btn-primary"
            data-testid="email-send-button"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
